import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import { CSSTransition } from 'react-transition-group';
import { Box } from '@leagueplatform/genesis-core';
import {
  STEP_DIRECTION_LEFT_TO_RIGHT,
  STEP_DIRECTION_RIGHT_TO_LEFT,
  STEP_TRANSITION_DIRECTION,
} from './constants';

export interface StepTransitionWrapperProps {
  children: ReactNode;
  in: boolean;
  timeout?: number;
  classNames?: string;
  unmountOnExit?: boolean;
  mountOnEnter?: boolean;
  nextIndex: number;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited?: () => void;
}

const getSlideTransitionWithTimeout = (timeout: number) =>
  `transform ${timeout}ms ease-in-out, opacity ${timeout}ms ease-in-out`;

export const StepTransitionWrapper: React.FC<StepTransitionWrapperProps> = ({
  children,
  in: inProp,
  timeout = 300,
  classNames,
  unmountOnExit = true,
  mountOnEnter = true,
  nextIndex,
  onEnter,
  onEntered,
  onExit,
  onExited,
}) => {
  const nodeRef = useRef(null);

  const prevActiveIndex = useRef(nextIndex);
  const direction = useRef<STEP_TRANSITION_DIRECTION>(
    STEP_DIRECTION_RIGHT_TO_LEFT,
  );
  if (nextIndex < prevActiveIndex.current) {
    direction.current = STEP_DIRECTION_LEFT_TO_RIGHT;
  } else if (nextIndex > prevActiveIndex.current) {
    direction.current = STEP_DIRECTION_RIGHT_TO_LEFT;
  }

  const transitionClassNames = classNames || `step-slide-${direction.current}`;

  const slideTransition = useMemo(
    () => getSlideTransitionWithTimeout(timeout),
    [timeout],
  );

  useEffect(() => {
    setTimeout(() => {
      prevActiveIndex.current = nextIndex;
    }, timeout);
  }, [nextIndex, prevActiveIndex, timeout]);

  return (
    <CSSTransition
      nodeRef={nodeRef}
      in={inProp}
      timeout={timeout}
      classNames={transitionClassNames}
      unmountOnExit={unmountOnExit}
      mountOnEnter={mountOnEnter}
      onEnter={onEnter}
      onEntered={onEntered}
      onExit={onExit}
      onExited={onExited}
    >
      <Box
        ref={nodeRef}
        css={{
          /* Base wrapper styles */
          '&.step-transition-wrapper': {
            width: '100%',
            height: '100%',
          },

          /* Left to Right Slide Transition */
          '&.step-slide-left-to-right-enter': {
            transform: 'translateX(-100%)',
            opacity: 0,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          '&.step-slide-left-to-right-enter-active': {
            transform: 'translateX(0%)',
            opacity: 1,
            transition: slideTransition,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          '&.step-slide-left-to-right-exit': {
            transform: 'translateX(0%)',
            opacity: 1,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          '&.step-slide-left-to-right-exit-active': {
            transform: 'translateX(100%)',
            opacity: 0,
            transition: slideTransition,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          /* Right to Left Slide Transition */
          '&.step-slide-right-to-left-enter': {
            transform: 'translateX(100%)',
            opacity: 0,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          '&.step-slide-right-to-left-enter-active': {
            transform: 'translateX(0%)',
            opacity: 1,
            transition: slideTransition,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          '&.step-slide-right-to-left-exit': {
            transform: 'translateX(0%)',
            opacity: 1,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          '&.step-slide-right-to-left-exit-active': {
            transform: 'translateX(-100%)',
            opacity: 0,
            transition: slideTransition,
            '@media (prefers-reduced-motion: reduce)': {
              transform: 'none',
            },
          },

          /* Ensure smooth transitions */
          '&.step-slide-left-to-right-enter-done, &.step-slide-right-to-left-enter-done':
            {
              transform: 'translateX(0%)',
              opacity: 1,
              '@media (prefers-reduced-motion: reduce)': {
                transform: 'none',
              },
            },
        }}
      >
        {children}
      </Box>
    </CSSTransition>
  );
};
